import React, { useEffect, useState } from "react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { Save, Mail, Cable, AlertCircle, CheckCircle2 } from "lucide-react";

export default function Configuracoes() {
    const [settings, setSettings] = useState(null);
    const [templates, setTemplates] = useState([]);
    const [saving, setSaving] = useState(false);
    const [toast, setToast] = useState(null);
    const [tab, setTab] = useState("retool");

    async function refresh() {
        const [s, t] = await Promise.all([api.get("/settings"), api.get("/email-templates")]);
        setSettings(s.data);
        setTemplates(t.data);
    }

    useEffect(() => { refresh(); }, []);

    async function saveSettings(updates) {
        setSaving(true);
        try {
            const { data } = await api.patch("/settings", updates);
            setSettings(data);
            setToast({ type: "ok", msg: "Configurações salvas." });
        } catch (e) {
            setToast({ type: "err", msg: formatApiErrorDetail(e?.response?.data?.detail) });
        } finally {
            setSaving(false);
            setTimeout(() => setToast(null), 3500);
        }
    }

    async function saveTemplate(key, updates) {
        setSaving(true);
        try {
            await api.patch(`/email-templates/${key}`, updates);
            await refresh();
            setToast({ type: "ok", msg: "Template atualizado." });
        } catch (e) {
            setToast({ type: "err", msg: formatApiErrorDetail(e?.response?.data?.detail) });
        } finally {
            setSaving(false);
            setTimeout(() => setToast(null), 3500);
        }
    }

    if (!settings) return <div className="text-sm text-slate-500">Carregando…</div>;

    return (
        <div className="space-y-6 fade-in max-w-4xl" data-testid="settings-page">
            <header>
                <div className="text-xs uppercase tracking-[0.22em] text-slate-500 font-medium">Administração</div>
                <h1 className="font-display font-bold text-3xl text-[#0F2044] mt-1">Configurações</h1>
                <p className="text-sm text-slate-500 mt-1">Integrações, limites dos alertas e templates de e-mail.</p>
            </header>

            <nav className="border-b border-slate-200 flex gap-1" data-testid="settings-tabs">
                {[
                    { id: "retool", label: "Integração Retool", icon: Cable },
                    { id: "alertas", label: "Limites de Alertas", icon: AlertCircle },
                    { id: "email", label: "E-mail (Resend)", icon: Mail },
                    { id: "templates", label: "Templates", icon: Mail },
                ].map((t) => (
                    <button key={t.id} onClick={() => setTab(t.id)} data-testid={`settings-tab-${t.id}`}
                        className={`px-4 py-2.5 text-sm font-medium border-b-2 transition-colors flex items-center gap-2 ${tab === t.id ? "border-[#0F2044] text-[#0F2044]" : "border-transparent text-slate-500 hover:text-[#0F2044]"}`}>
                        <t.icon className="h-3.5 w-3.5" /> {t.label}
                    </button>
                ))}
            </nav>

            {toast && (
                <div className={`px-4 py-2.5 rounded-md text-sm flex items-center gap-2 ${toast.type === "ok" ? "bg-emerald-50 border border-emerald-200 text-emerald-800" : "bg-red-50 border border-red-200 text-red-800"}`} data-testid="settings-toast">
                    {toast.type === "ok" ? <CheckCircle2 className="h-4 w-4" /> : <AlertCircle className="h-4 w-4" />}
                    {toast.msg}
                </div>
            )}

            {tab === "retool" && <RetoolForm settings={settings} onSave={saveSettings} saving={saving} />}
            {tab === "alertas" && <AlertasForm settings={settings} onSave={saveSettings} saving={saving} />}
            {tab === "email" && <EmailConfigForm settings={settings} onSave={saveSettings} saving={saving} />}
            {tab === "templates" && <TemplatesForm templates={templates} onSave={saveTemplate} saving={saving} />}
        </div>
    );
}

function RetoolForm({ settings, onSave, saving }) {
    const [form, setForm] = useState({
        retool_url: settings.retool_url || "",
        retool_token: settings.retool_token || "",
    });
    return (
        <div className="bg-white border border-slate-200 rounded-lg p-6 space-y-4" data-testid="retool-form">
            <div className="bg-blue-50 border border-blue-200 rounded-md px-4 py-3 text-sm text-blue-900">
                Aqui você pluga o webhook/REST do Retool que retorna métricas reais de uso por cliente da licito.guru.
                Enquanto não configurar, os indicadores de uso seguem em modo manual (a aba &quot;Indicadores de Uso&quot; do contrato).
            </div>
            <Field label="URL do Retool (REST)"><input value={form.retool_url} onChange={(e) => setForm({ ...form, retool_url: e.target.value })} data-testid="retool-url-input" className="input" placeholder="https://api.retool.com/v1/workflows/..." /></Field>
            <Field label="Token de autenticação"><input value={form.retool_token} onChange={(e) => setForm({ ...form, retool_token: e.target.value })} data-testid="retool-token-input" className="input" placeholder="••••••••" type="password" /></Field>
            <div className="text-xs text-slate-500 bg-slate-50 border border-slate-100 rounded-md p-3">
                <strong>Mapeamento de campos esperados</strong> (ajustar conforme payload do Retool):
                <ul className="mt-2 space-y-0.5 font-mono">
                    {Object.entries(settings.retool_field_map || {}).map(([k, v]) => (
                        <li key={k}>{k} ← <span className="text-[#0F2044]">{v}</span></li>
                    ))}
                </ul>
            </div>
            <SaveButton onClick={() => onSave(form)} saving={saving} />
        </div>
    );
}

function AlertasForm({ settings, onSave, saving }) {
    const [form, setForm] = useState({
        sem_login_days: settings.sem_login_days,
        sem_uso_days: settings.sem_uso_days,
        sem_interacao_days: settings.sem_interacao_days,
        drop_pct_threshold: settings.drop_pct_threshold,
        critico_persistence_days: settings.critico_persistence_days,
        atencao_persistence_days: settings.atencao_persistence_days,
        alto_uso_pct: settings.alto_uso_pct,
        renovacao_window_days: settings.renovacao_window_days,
    });
    return (
        <div className="bg-white border border-slate-200 rounded-lg p-6 grid grid-cols-2 gap-4" data-testid="alertas-form">
            <Numeric label="Sem login (dias)" value={form.sem_login_days} onChange={(v) => setForm({ ...form, sem_login_days: v })} testid="alert-sem-login" />
            <Numeric label="Sem uso (dias)" value={form.sem_uso_days} onChange={(v) => setForm({ ...form, sem_uso_days: v })} testid="alert-sem-uso" />
            <Numeric label="Sem interação (dias)" value={form.sem_interacao_days} onChange={(v) => setForm({ ...form, sem_interacao_days: v })} testid="alert-sem-interacao" />
            <Numeric label="Queda de uso (%)" value={form.drop_pct_threshold} onChange={(v) => setForm({ ...form, drop_pct_threshold: v })} testid="alert-drop-pct" />
            <Numeric label="Crítico há mais de (dias)" value={form.critico_persistence_days} onChange={(v) => setForm({ ...form, critico_persistence_days: v })} testid="alert-critico-persist" />
            <Numeric label="Atenção há mais de (dias)" value={form.atencao_persistence_days} onChange={(v) => setForm({ ...form, atencao_persistence_days: v })} testid="alert-atencao-persist" />
            <Numeric label="Alto uso (%)" value={form.alto_uso_pct} onChange={(v) => setForm({ ...form, alto_uso_pct: v })} testid="alert-alto-uso" />
            <Numeric label="Janela de renovação (dias)" value={form.renovacao_window_days} onChange={(v) => setForm({ ...form, renovacao_window_days: v })} testid="alert-renovacao-window" />
            <div className="col-span-2"><SaveButton onClick={() => onSave(form)} saving={saving} /></div>
        </div>
    );
}

function EmailConfigForm({ settings, onSave, saving }) {
    const [form, setForm] = useState({
        sender_email: settings.sender_email || "onboarding@resend.dev",
        resend_api_key: "",
    });
    const configured = settings.resend_api_key_configured;
    return (
        <div className="bg-white border border-slate-200 rounded-lg p-6 space-y-4" data-testid="email-form">
            <div className={`px-4 py-3 rounded-md text-sm border ${configured ? "bg-emerald-50 border-emerald-200 text-emerald-800" : "bg-amber-50 border-amber-200 text-amber-800"}`}>
                {configured ? "✓ RESEND_API_KEY está configurada." : "⚠ RESEND_API_KEY ainda não configurada — o botão 'Enviar Boas-vindas' ficará bloqueado."}
            </div>
            <Field label="RESEND_API_KEY">
                <input value={form.resend_api_key} onChange={(e) => setForm({ ...form, resend_api_key: e.target.value })}
                    data-testid="resend-key-input" className="input" type="password" placeholder={configured ? "••••••••• (preencha apenas para trocar)" : "re_xxxxxxxxxxxx"} />
                <p className="text-xs text-slate-500 mt-1">Crie em <a href="https://resend.com/api-keys" target="_blank" rel="noreferrer" className="underline">resend.com/api-keys</a>. A chave é guardada criptografada no banco e não é exposta nas respostas da API.</p>
            </Field>
            <Field label="E-mail remetente (From)">
                <input value={form.sender_email} onChange={(e) => setForm({ ...form, sender_email: e.target.value })} data-testid="sender-email-input" className="input" placeholder="cs@licito.guru" />
                <p className="text-xs text-slate-500 mt-1">Use <code>onboarding@resend.dev</code> para testes (Resend só permite enviar para o e-mail da sua conta enquanto o domínio não estiver verificado).</p>
            </Field>
            <SaveButton onClick={() => onSave(form)} saving={saving} />
        </div>
    );
}

function TemplatesForm({ templates, onSave, saving }) {
    return (
        <div className="space-y-4" data-testid="templates-form">
            {templates.map((t) => (
                <TemplateEditor key={t.key} tpl={t} onSave={(u) => onSave(t.key, u)} saving={saving} />
            ))}
        </div>
    );
}

function TemplateEditor({ tpl, onSave, saving }) {
    const [subject, setSubject] = useState(tpl.subject);
    const [bodyHtml, setBodyHtml] = useState(tpl.body_html);
    return (
        <div className="bg-white border border-slate-200 rounded-lg p-6 space-y-3" data-testid={`template-${tpl.key}`}>
            <div className="flex items-center justify-between">
                <h3 className="font-display font-semibold text-base text-[#0F2044]">{tpl.name}</h3>
                <span className="text-xs text-slate-500">Variáveis: {tpl.variables.map(v => `{{${v}}}`).join(" · ")}</span>
            </div>
            <Field label="Assunto"><input value={subject} onChange={(e) => setSubject(e.target.value)} data-testid={`tpl-${tpl.key}-subject`} className="input" /></Field>
            <Field label="Corpo (HTML)"><textarea value={bodyHtml} onChange={(e) => setBodyHtml(e.target.value)} rows={14} data-testid={`tpl-${tpl.key}-body`} className="input font-mono text-xs" /></Field>
            <div className="flex justify-end">
                <SaveButton onClick={() => onSave({ subject, body_html: bodyHtml })} saving={saving} />
            </div>
        </div>
    );
}

function Field({ label, children }) {
    return (
        <div>
            <label className="block text-[10px] uppercase tracking-[0.16em] text-slate-500 font-semibold mb-1">{label}</label>
            {children}
        </div>
    );
}

function Numeric({ label, value, onChange, testid }) {
    return (
        <div>
            <label className="block text-[10px] uppercase tracking-[0.16em] text-slate-500 font-semibold mb-1">{label}</label>
            <input type="number" min={0} value={value ?? 0} onChange={(e) => onChange(Number(e.target.value))} data-testid={testid} className="input" />
        </div>
    );
}

function SaveButton({ onClick, saving }) {
    return (
        <button onClick={onClick} disabled={saving} data-testid="settings-save-button"
            className="inline-flex items-center gap-2 bg-[#0F2044] hover:bg-[#172E5E] text-white text-sm font-semibold px-4 py-2 rounded-md disabled:opacity-60">
            <Save className="h-4 w-4" /> {saving ? "Salvando…" : "Salvar"}
        </button>
    );
}
